<template>
  <div class="min-h-screen bg-zinc-50 dark:bg-black text-zinc-900 dark:text-zinc-100 selection:bg-brand-500/20 font-sans transition-colors duration-300">
    <div class="fixed inset-0 z-0 pointer-events-none overflow-hidden">
      <div class="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-brand-200/40 dark:bg-brand-600/20 blur-[120px] rounded-full opacity-60 dark:opacity-40 mix-blend-multiply dark:mix-blend-screen transition-colors duration-500"></div>
      <div class="absolute bottom-0 left-0 w-full h-[300px] bg-gradient-to-t from-zinc-100/50 dark:from-brand-900/10 to-transparent opacity-50 dark:opacity-30"></div>
    </div>

    <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-8 lg:py-20">
      
      <header class="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 lg:mb-12">
        <div class="space-y-2">
          <nav class="flex items-center gap-2 text-xs font-medium text-zinc-500 dark:text-zinc-500 uppercase tracking-wider">
            <span class="hover:text-zinc-800 dark:hover:text-zinc-300 transition-colors cursor-pointer">Settings</span>
            <span class="text-zinc-300 dark:text-zinc-700">/</span>
            <span class="text-brand-600 dark:text-brand-500">Billing</span>
          </nav>
          <h1 class="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900 dark:text-white">
            Subscription
          </h1>
          <p class="text-zinc-500 dark:text-zinc-400 max-w-lg text-sm leading-relaxed">
            Manage your plan, billing details, and invoices.
          </p>
        </div>

        <div v-if="!loadingStatus && status.subscription" class="self-start md:self-center flex items-center gap-3 px-4 py-2 rounded-full bg-white/60 dark:bg-zinc-900/80 border border-zinc-200 dark:border-zinc-800 backdrop-blur-md shadow-sm dark:shadow-none">
          <div class="relative flex h-2.5 w-2.5">
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
            <span class="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
          </div>
          <span class="text-xs font-medium text-zinc-700 dark:text-zinc-300">System Operational</span>
        </div>
      </header>

      <div v-if="loadingStatus" class="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-pulse">
        <div class="lg:col-span-8 h-96 rounded-3xl bg-zinc-200 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800/50"></div>
        <div class="lg:col-span-4 h-96 rounded-3xl bg-zinc-200 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800/50"></div>
      </div>

      <div v-else class="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
        
        <section class="lg:col-span-8 space-y-6 lg:space-y-8">
          
          <div class="relative overflow-hidden rounded-3xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40 backdrop-blur-xl p-6 sm:p-8 lg:p-10 transition-all shadow-sm hover:shadow-md dark:shadow-none dark:hover:border-zinc-700/50 group">
            <div class="hidden dark:block absolute -inset-px bg-gradient-to-r from-brand-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>

            <div v-if="status.subscription" class="relative z-10">
              <div class="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-8">
                <div>
                  <h2 class="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-transparent dark:bg-clip-text dark:bg-gradient-to-br dark:from-white dark:to-zinc-500">
                    {{ status.subscription.plan.name }}
                  </h2>
                  <p class="mt-2 text-sm text-zinc-500 dark:text-zinc-500 font-medium tracking-wide flex items-center gap-2">
                    <span class="w-1.5 h-1.5 rounded-full" :class="status.subscription.cancelAtPeriodEnd ? 'bg-amber-500' : 'bg-emerald-500'"></span>
                    {{ status.subscription.cancelAtPeriodEnd ? 'CANCELLING SOON' : 'ACTIVE SUBSCRIPTION' }}
                  </p>
                </div>
                <div class="flex items-baseline gap-1 sm:block sm:text-right">
                  <div class="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900 dark:text-white">
                    {{ money(status.subscription.plan.amount, status.subscription.plan.currency) }}
                  </div>
                  <div class="text-sm text-zinc-500 mt-1">
                    / {{ status.subscription.plan.interval }}
                  </div>
                </div>
              </div>

              <div class="mb-8 p-4 rounded-2xl bg-zinc-50 dark:bg-black/40 border border-zinc-100 dark:border-zinc-800/50">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                   <div class="flex items-center gap-2">
                      <span class="material-symbols-outlined text-zinc-400 text-lg">credit_card</span>
                      <span class="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Payment Method</span>
                   </div>
                   <div class="text-sm text-zinc-900 dark:text-zinc-200 flex items-center gap-2">
                      Managed via Stripe <span class="material-symbols-outlined text-sm text-zinc-400">open_in_new</span>
                   </div>
                </div>
              </div>

              <div class="flex flex-col sm:flex-row gap-3">
                 <button 
                  @click="openPortalFocused(status.subscription.stripeSubscriptionId)"
                  :disabled="portalLoading"
                  class="w-full sm:w-auto sm:flex-1 inline-flex items-center justify-center px-6 py-3.5 rounded-xl bg-zinc-900 dark:bg-white text-white dark:text-black text-sm font-semibold hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors disabled:opacity-50 shadow-sm"
                >
                  {{ portalLoading ? 'Processing...' : 'Change Plan' }}
                </button>
                <button 
                  @click="openPortal()"
                  :disabled="portalLoading"
                  class="w-full sm:w-auto sm:flex-1 inline-flex items-center justify-center px-6 py-3.5 rounded-xl bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-200 text-sm font-medium border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50"
                >
                  Manage Billing
                </button>
              </div>
            </div>

            <div v-else class="relative z-10 py-4 sm:py-6 text-center lg:text-left lg:flex lg:items-center lg:justify-between lg:gap-10">
              <div class="max-w-md mx-auto lg:mx-0">
                <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-100 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700 text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-4">
                  <span class="w-1.5 h-1.5 rounded-full bg-zinc-400 dark:bg-zinc-500"></span>
                  Currently on Free Plan
                </div>
                <h2 class="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-white mb-3">Upgrade to Pro</h2>
                <p class="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed mb-6">
                  Unlock unlimited projects, advanced analytics, and priority support. 
                  Take your productivity to the next level.
                </p>
                <button 
                  @click="startSubscription"
                  :disabled="starting"
                  class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl bg-gradient-to-r from-zinc-900 to-zinc-800 dark:from-brand-600 dark:to-brand-500 text-white font-semibold shadow-lg shadow-zinc-500/20 dark:shadow-brand-900/20 hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-70 disabled:transform-none"
                >
                  <span v-if="starting" class="material-symbols-outlined animate-spin text-lg">sync</span>
                  <span>{{ starting ? 'Redirecting...' : 'Upgrade Now' }}</span>
                  <span class="material-symbols-outlined text-lg">arrow_forward</span>
                </button>
              </div>
              
              <div class="hidden lg:block relative w-48 h-48 flex-shrink-0">
                <div class="absolute inset-0 bg-brand-200/50 dark:bg-brand-500/20 blur-3xl rounded-full"></div>
                <div class="relative h-full w-full rounded-2xl border border-zinc-100 dark:border-zinc-800 bg-white dark:bg-zinc-900/80 shadow-sm dark:shadow-none flex items-center justify-center">
                   <span class="material-symbols-outlined text-6xl text-brand-600 dark:text-brand-500/50">rocket_launch</span>
                </div>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="p-5 sm:p-6 rounded-3xl bg-white dark:bg-zinc-900/20 border border-zinc-200 dark:border-zinc-800/60 hover:border-zinc-300 dark:hover:border-zinc-700/60 transition-colors shadow-sm dark:shadow-none">
              <div class="w-10 h-10 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                <span class="material-symbols-outlined text-zinc-600 dark:text-zinc-400">receipt_long</span>
              </div>
              <h3 class="text-lg font-medium text-zinc-900 dark:text-zinc-200">Invoices & Receipts</h3>
              <p class="mt-2 text-sm text-zinc-500 leading-relaxed">
                Download your monthly invoices for tax purposes directly from the Stripe portal.
              </p>
              <button @click="openPortal()" class="mt-4 text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 flex items-center gap-1 group">
                View history <span class="material-symbols-outlined text-sm transition-transform group-hover:translate-x-0.5">chevron_right</span>
              </button>
            </div>

            <div class="p-5 sm:p-6 rounded-3xl bg-white dark:bg-zinc-900/20 border border-zinc-200 dark:border-zinc-800/60 hover:border-zinc-300 dark:hover:border-zinc-700/60 transition-colors shadow-sm dark:shadow-none">
               <div class="w-10 h-10 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                <span class="material-symbols-outlined text-zinc-600 dark:text-zinc-400">shield_lock</span>
              </div>
              <h3 class="text-lg font-medium text-zinc-900 dark:text-zinc-200">Secure Billing</h3>
              <p class="mt-2 text-sm text-zinc-500 leading-relaxed">
                All payments are processed securely by Stripe. We never store your credit card details.
              </p>
               <RouterLink :to="{ name: 'privacy' }" class="mt-4 text-sm font-medium text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white flex items-center gap-1 group">
                Privacy Policy <span class="material-symbols-outlined text-sm transition-transform group-hover:translate-x-0.5">chevron_right</span>
              </RouterLink>
            </div>
          </div>
        </section>

        <aside class="lg:col-span-4 space-y-6">
          <div class="rounded-3xl border border-zinc-200 dark:border-zinc-800 bg-white/50 dark:bg-zinc-900/30 p-1 backdrop-blur-md shadow-sm dark:shadow-none">
            <div class="px-5 py-4 sm:p-5">
               <h3 class="text-sm font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-1">Actions</h3>
            </div>
            <nav class="flex flex-col gap-1 p-1">
              <button 
                @click="openPortal()" 
                :disabled="portalLoading"
                class="flex items-center justify-between w-full p-4 rounded-2xl hover:bg-zinc-100 dark:hover:bg-zinc-800/50 transition-colors text-left group"
              >
                <div class="flex items-center gap-3">
                  <div class="flex items-center justify-center w-8 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 group-hover:text-zinc-900 dark:group-hover:text-white transition-colors">
                    <span class="material-symbols-outlined text-lg">payment</span>
                  </div>
                  <div>
                    <div class="text-sm font-medium text-zinc-700 dark:text-zinc-200 group-hover:text-zinc-900 dark:group-hover:text-white">Payment Methods</div>
                    <div class="text-xs text-zinc-500">Update card or bank</div>
                  </div>
                </div>
                <span class="material-symbols-outlined text-zinc-400 dark:text-zinc-600 group-hover:text-zinc-600 dark:group-hover:text-zinc-400">chevron_right</span>
              </button>

              <button 
                v-if="status.subscription"
                @click="openPortalFocused(status.subscription?.stripeSubscriptionId)"
                :disabled="portalLoading"
                class="flex items-center justify-between w-full p-4 rounded-2xl hover:bg-zinc-100 dark:hover:bg-zinc-800/50 transition-colors text-left group"
              >
                <div class="flex items-center gap-3">
                  <div class="flex items-center justify-center w-8 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 group-hover:text-zinc-900 dark:group-hover:text-white transition-colors">
                    <span class="material-symbols-outlined text-lg">swap_vert</span>
                  </div>
                  <div>
                    <div class="text-sm font-medium text-zinc-700 dark:text-zinc-200 group-hover:text-zinc-900 dark:group-hover:text-white">Upgrade or Downgrade</div>
                    <div class="text-xs text-zinc-500">Change your plan</div>
                  </div>
                </div>
                <span class="material-symbols-outlined text-zinc-400 dark:text-zinc-600 group-hover:text-zinc-600 dark:group-hover:text-zinc-400">chevron_right</span>
              </button>
              
               <button 
                v-if="status.subscription"
                @click="openPortal()"
                :disabled="portalLoading"
                class="flex items-center justify-between w-full p-4 rounded-2xl hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors text-left group"
              >
                <div class="flex items-center gap-3">
                  <div class="flex items-center justify-center w-8 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                    <span class="material-symbols-outlined text-lg">cancel</span>
                  </div>
                  <div>
                    <div class="text-sm font-medium text-zinc-700 dark:text-zinc-200 group-hover:text-red-700 dark:group-hover:text-red-300">Cancel Subscription</div>
                    <div class="text-xs text-zinc-500">End current billing</div>
                  </div>
                </div>
                <span class="material-symbols-outlined text-zinc-400 dark:text-zinc-600 group-hover:text-red-500 dark:group-hover:text-red-400">chevron_right</span>
              </button>
            </nav>
          </div>

          <div class="rounded-3xl bg-zinc-100 dark:bg-gradient-to-br dark:from-zinc-800 dark:to-zinc-900/50 p-6 border border-zinc-200 dark:border-zinc-700/50 relative overflow-hidden">
             <div class="absolute top-0 right-0 p-4 opacity-5 dark:opacity-10">
                <span class="material-symbols-outlined text-8xl text-black dark:text-white">support_agent</span>
             </div>
             <h4 class="text-sm font-semibold text-zinc-900 dark:text-white relative z-10">Need help?</h4>
             <p class="mt-2 text-xs text-zinc-600 dark:text-zinc-400 relative z-10 leading-relaxed">
               Issues with your payment or questions about the Enterprise plan? Our team is ready to help.
             </p>
             <a href="mailto:support@nexgridco.com" class="inline-flex items-center gap-2 mt-4 text-xs font-semibold text-brand-600 dark:text-brand-300 hover:text-brand-700 dark:hover:text-brand-200 relative z-10">
               Contact Support <span class="material-symbols-outlined text-sm">arrow_outward</span>
             </a>
          </div>

          <div v-if="error" class="rounded-2xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 p-4">
             <div class="flex items-start gap-3">
                <span class="material-symbols-outlined text-red-600 dark:text-red-400 text-lg mt-0.5">error</span>
                <div>
                   <h4 class="text-sm font-medium text-red-800 dark:text-red-200">Error</h4>
                   <p class="text-xs text-red-700 dark:text-red-300/80 mt-1">{{ error }}</p>
                </div>
             </div>
          </div>
        </aside>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { RouterLink } from 'vue-router'

const API = 'https://backend.nexgridco.com'

const loadingStatus = ref(true)
const portalLoading = ref(false)
const starting = ref(false)
const error = ref('')

const status = ref({ hasActive: false, subscription: null })

function token() {
  try {
    return localStorage.getItem('token') || sessionStorage.getItem('token') || ''
  } catch {
    return ''
  }
}

function authHeaders() {
  const t = token()
  return t
    ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' }
}

function money(cents, currency = 'BRL') {
  if (typeof cents !== 'number') return '—'
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100)
  } catch {
    return `R$ ${(cents / 100).toFixed(2)}`
  }
}

async function loadStatus() {
  loadingStatus.value = true
  error.value = ''
  try {
    const res = await fetch(`${API}/billing/status`, {
      headers: authHeaders(),
      credentials: 'include'
    })
    if (!res.ok) {
      error.value = await res.text()
      return
    }
    status.value = await res.json()
  } catch {
    error.value = 'Failed to load status'
  } finally {
    loadingStatus.value = false
  }
}

async function syncNow() {
  try {
    await fetch(`${API}/billing/sync`, { headers: authHeaders(), credentials: 'include' })
  } catch {
    //
  }
}

async function openPortal(body = {}) {
  portalLoading.value = true
  error.value = ''
  try {
    const res = await fetch(`${API}/billing/portal`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(body)
    })
    if (!res.ok) {
      error.value = await res.text()
      return
    }
    const data = await res.json()
    if (data?.url) window.location.href = data.url
    else error.value = 'Portal URL not received'
  } catch {
    error.value = 'Failed to open portal'
  } finally {
    portalLoading.value = false
  }
}

function openPortalFocused(subId) {
  if (!subId) return openPortal()
  return openPortal({ subscriptionId: subId })
}

async function startSubscription() {
  starting.value = true
  error.value = ''
  try {
    const res = await fetch(`${API}/billing/ensure`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify({})
    })
    if (!res.ok) {
      error.value = await res.text()
      starting.value = false
      return
    }
    const data = await res.json()
    if (data?.url) window.location.href = data.url
    else error.value = 'Checkout URL not received'
  } catch {
    error.value = 'Failed to start subscription'
  } finally {
    starting.value = false
  }
}

onMounted(async () => {
  const url = new URL(window.location.href)
  if (url.searchParams.get('synced') === '1') {
    await syncNow()
    url.searchParams.delete('synced')
    window.history.replaceState({}, '', url.toString())
  }
  await loadStatus()
})
</script>

